import uhyphen from 'uhyphen'

// TINY LODASH REPLACEMENT

// types

export function isNil(input) {
  return input === null || input === undefined
}

export function isNull(input) {
  return input === null
}

export function isNaN(input) {
  return Number.isNaN(input)
}

export function isSet(input) {
  return input instanceof Set
}

export function isMap(input) {
  return input instanceof Map
}

export function isError(input) {
  return input instanceof Error
}

export function isDate(input) {
  return input instanceof Date
}

export function isArray(input) {
  return Array.isArray(input)
}

export function isRegExp(input) {
  return input instanceof RegExp
}

export function isSymbol(input) {
  return typeof input === 'symbol'
}

export function isIterator(input) {
  return !!input?.[Symbol.iterator]
}

export function isNumber(input) {
  return typeof input === 'number'
}

export function isFinite(input) {
  return Number.isFinite(input)
}

export function isString(input) {
  return typeof input === 'string'
}

export function isFunction(input) {
  return typeof input === 'function'
}

export function isBoolean(value) {
  return value === true || value === false
}

export function isUndefined(value) {
  return value === undefined
}

export function isElement(input) {
  return input?.nodeType === 1
}

export function isPromise(probe) {
  if (!probe) {
    return false
  } else {
    return isFunction(probe.then)
  }
}

export function isPrimitive(input) {
  // also bigint but we never use them
  return isNil(input) || isString(input) || isNumber(input) || isBoolean(input) || isSymbol(input)
}

export function isArguments(input) {
  return Object.prototype.toString.call(input) === '[object Arguments]'
}

export function isPlainObject(input) {
  if (!isObjectLike(input)) {
    return false
  }

  const proto = Object.getPrototypeOf(input)
  return proto === null || proto === Object.prototype
}

export function isObjectLike(input) {
  return input !== null && typeof input === 'object'
}

export function isArrayLikeObject(input) {
  return isObjectLike(input) && isNumber(input.length)
}

// strings

export function toString(input) {
  if (isString(input)) {
    return input
  } else if (isNil(input)) {
    return '' + input
  } else {
    return input.toString()
  }
}

export function trim(input) {
  if (isNil(input)) {
    return ''
  }

  return toString(input).trim()
}

export function upperFirst(input) {
  if (isNil(input)) {
    return ''
  }

  const string = toString(input)

  if (string.length === 0) {
    return ''
  }

  return string[0].toUpperCase() + string.slice(1)
}

export function toLower(input) {
  if (isNil(input)) {
    return ''
  }

  return toString(input).toLowerCase()
}

export function toSafeString(input) {
  if (isNil(input)) {
    return ''
  }

  return toString(input).replace(/[^A-Za-z0-9]/g, '')
}

export function kebabCase(input) {
  if (isNil(input)) {
    return ''
  }

  const camelCaseLike = toString(input).replace(/[^a-z0-9]+[a-z0-9]/ig, (bit) => {
    return bit.at(-1).toUpperCase()
  })

  return uhyphen(camelCaseLike)
}

export function snakeCase(input) {
  return kebabCase(input).replace(/-/g, '_')
}

export function words(input) {
  if (isNil(input)) {
    return []
  }

  return toString(input).trim().split(/\s+/)
}

// objects

export function combine(...sources) {
  return assign({}, ...sources)
}

export function assign(target, ...sources) {
  target = Object(target)

  for (const source of sources) {
    Object.assign(target, source)
  }

  return target
}

export function pick(obj, ...keys) {
  if (isNil(obj)) {
    return {}
  }

  if (!isObjectLike(obj)) {
    console.warn(`pick: unintended use with ${obj}`)
  }

  if (isArray(keys[0])) {
    keys = keys[0]
  }

  const result = {}

  for (const key of keys) {
    if (hasIn(obj, key)) {
      result[key] = obj[key]
    }
  }

  return result
}

export function pickBy(obj, predicate = identity) {
  if (isNil(obj)) {
    return {}
  }

  if (!isObjectLike(obj)) {
    console.warn(`pickBy: unintended use with ${obj}`)
  }

  const result = {}

  for (const [key, value] of entries(obj)) {
    if (predicate(value, key)) {
      result[key] = value
    }
  }

  return result
}

export function omit(obj, ...unwantedKeys) {
  if (isNil(obj)) {
    return {}
  }

  if (isArray(unwantedKeys[0])) {
    unwantedKeys = unwantedKeys[0]
  }

  for (let index = 0, length = unwantedKeys.length; index !== length; index++) {
    unwantedKeys[index] = toString(unwantedKeys[index])
  }

  if (!isObjectLike(obj)) {
    console.warn(`omit: unintended use with ${obj}`)
  }

  const result = {}

  for (const [key, value] of entries(obj)) {
    if (!unwantedKeys.includes(key)) {
      result[key] = value
    }
  }

  return result
}

export function has(obj, key) {
  if (isNil(obj)) {
    return false
  }

  return Object.prototype.hasOwnProperty.call(obj, key)
}

export function hasIn(obj, key) {
  if (isPrimitive(obj)) {
    return false
  }

  return Reflect.has(obj, key)
}

export function hasAll(obj, ...keys) {
  if (isNil(obj) || keys.length === 0) {
    return false
  }

  if (isArray(keys[0])) {
    keys = keys[0]
  }

  for (const key of keys) {
    if (!has(obj, key)) {
      return false
    }
  }

  return true
}

export function hasAny(obj, ...keys) {
  if (isNil(obj) || keys.length === 0) {
    return false
  }

  if (isArray(keys[0])) {
    keys = keys[0]
  }

  for (const key of keys) {
    if (has(obj, key)) {
      return true
    }
  }

  return false
}

export function hasOnly(obj, ...source) {
  if (isNil(obj) || source.length === 0) {
    return false
  }

  if (isArray(source[0])) {
    source = source[0]
  }

  const target = keys(obj)

  if (target.length === 0 || target.length > source.length) {
    return false
  }

  for (let index = 0, length = source.length; index !== length; index++) {
    source[index] = toString(source[index])
  }

  for (const key of source) {
    const index = target.indexOf(key)
    if (index !== -1) {
      target.splice(index, 1)
    }
  }

  return target.length === 0
}

export function get(obj, key) {
  if (isNil(obj)) {
    return
  }

  return obj[key]
}

export function getAny(obj, ...keys) {
  if (isNil(obj) || keys.length === 0) {
    return
  }

  if (isArray(keys[0])) {
    keys = keys[0]
  }

  for (const key of keys) {
    if (has(obj, key)) {
      return obj[key]
    }
  }
}

export function keys(obj) {
  if (isNil(obj)) {
    return []
  }

  return Object.keys(obj)
}

export function values(obj) {
  if (isNil(obj)) {
    return []
  }

  return Object.values(obj)
}

export function entries(obj) {
  if (isNil(obj)) {
    return []
  }

  return Object.entries(obj)
}

export function fromEntries(pairs) {
  if (isNil(pairs)) {
    return {}
  }

  return Object.fromEntries(pairs)
}

export function hasValues(obj) {
  return values(obj).length !== 0
}

export function isEmpty(input) {
  if (isNil(input)) {
    return true
  } else if (isString(input) || isArrayLikeObject(input)) {
    return input.length === 0
  } else if (isSet(input) || isMap(input)) {
    return input.size === 0
  } else if (isObjectLike(input)) {
    return !hasValues(input)
  } else {
    console.warn(`isEmpty: unintended use with ${input}`)
    return true
  }
}

export function isBlank(input) {
  // does not treat false or 0 as blank
  if (isNil(input)) return true
  if (isNaN(input)) return true
  if (input === '') return true
  if (isObjectLike(input) && isEmpty(input)) return true

  return false
}

export function isPresent(input) {
  return !isBlank(input)
}

export function transform(input, iteratee, accumulator) {
  if (isNil(accumulator)) {
    accumulator = isArray(input) ? [] : {}
  }

  if (isNil(input)) {
    return accumulator
  }

  if (!iteratee) {
    return input
  }

  if (!isObjectLike(input)) {
    console.warn(`transform: unintended use with ${input}`)
  }

  if (isArray(input)) {
    for (let index = 0, length = input.length; index !== length; index++) {
      iteratee(accumulator, input[index], index)
    }
  } else {
    for (const [key, value] of entries(input)) {
      iteratee(accumulator, value, key)
    }
  }

  return accumulator
}

export function mapValues(input, iteratee) {
  if (isNil(input)) {
    return {}
  }

  if (!iteratee) {
    return input
  }

  if (!isObjectLike(input)) {
    console.warn(`mapValues: unintended use with ${input}`)
  }

  if (isArray(input)) {
    const accumulator = []

    for (let index = 0, length = input.length; index !== length; index++) {
      accumulator.push(iteratee(input[index], index))
    }

    return accumulator
  } else {
    const accumulator = {}

    for (const [key, value] of entries(input)) {
      accumulator[key] = iteratee(value, key, input)
    }

    return accumulator
  }
}

export function mapKeys(input, iteratee) {
  if (isNil(input)) {
    return {}
  }

  if (!iteratee) {
    return input
  }

  if (!isObjectLike(input)) {
    console.warn(`mapKeys: unintended use with ${input}`)
  }

  const accumulator = {}

  for (const [key, value] of entries(input)) {
    accumulator[iteratee(value, key, input)] = value
  }

  return accumulator
}

export function zipObject(keys, values) {
  const accumulator = {}

  if (isNil(keys)) {
    return accumulator
  }

  if (!isArrayLikeObject(keys)) {
    console.warn(`zipObject: unintended use with ${keys}`)
    return accumulator
  }

  for (let index = 0, length = keys.length; index !== length; index++) {
    accumulator[keys[index]] = get(values, index)
  }

  return accumulator
}

// arrays

export function wrapArray(input) {
  if (isNil(input)) {
    return []
  } else if (Array.isArray(input)) {
    return input.concat()
  } else if ((isIterator(input) && !isString(input)) || isArrayLikeObject(input)) {
    return Array.from(input)
  } else {
    return [input]
  }
}

export function keyBy(array, iteratee) {
  const accumulator = {}

  if (isNil(array) || isNil(iteratee)) {
    return accumulator
  }

  if (!isArrayLikeObject(array)) {
    console.warn(`keyBy: unintended use with ${array}`)
    return accumulator
  }

  for (let index = 0, length = array.length; index !== length; index++) {
    const val = array[index]
    const key = isFunction(iteratee) ? iteratee(val, index) : val[iteratee]
    accumulator[key] = val
  }

  return accumulator
}

export function groupBy(array, iteratee) {
  const accumulator = {}

  if (isNil(array) || isNil(iteratee)) {
    return accumulator
  }

  if (!isArrayLikeObject(array)) {
    console.warn(`groupBy: unintended use with ${array}`)
    return accumulator
  }

  for (let index = 0, length = array.length; index !== length; index++) {
    const val = array[index]
    const key = isFunction(iteratee) ? iteratee(val, index) : val[iteratee]

    if (!accumulator[key]) {
      accumulator[key] = []
    }

    accumulator[key].push(val)
  }

  return accumulator
}

export function sortBy(array, iteratee) {
  if (isNil(array)) {
    return []
  }

  if (!isArray(array)) {
    console.warn(`sortBy: unintended use with ${array}`)
    return []
  }

  if (isNil(iteratee)) {
    return array
  }

  const map = new Map()

  for (let index = 0, length = array.length; index !== length; index++) {
    const value = array[index]
    const weight = isFunction(iteratee) ? iteratee(value, index) : value[iteratee]
    map.set(value, weight)
  }

  return array.toSorted((valA, valB) => {
    const weightA = map.get(valA)
    const weightB = map.get(valB)
    if (weightA === weightB) {
      return 0
    } else if (weightA > weightB) {
      return 1
    } else {
      return -1
    }
  })
}

export function sortAs(target, reference) {
  if (isNil(target)) {
    return []
  }

  if (isNil(reference)) {
    return target
  }

  if (!isArray(reference)) {
    console.warn(`sortAs: unintended use with ${reference}`)
    return target
  }

  return sortBy(target, (value) => {
    return reference.indexOf(value)
  })
}

export function pull(array, value) {
  if (isNil(array)) {
    return []
  }

  if (!isArray(array)) {
    console.warn(`pull: unintended use with ${array}`)
    return array
  }

  const index = array.indexOf(value)

  if (index !== -1) {
    array.splice(index, 1)
  }

  return array
}

export function pushArray(target, source) {
  if (isNil(target)) {
    return []
  }

  if (!isArray(target)) {
    console.warn(`pushArray: unintended use with ${target}`)
    return []
  }

  if (isNil(source)) {
    return target
  }

  if (!isArrayLikeObject(source)) {
    console.warn(`pushArray: unintended use with ${source}`)
    return target
  }

  for (let index = 0, length = source.length; index !== length; index++) {
    target.push(source[index])
  }

  return target
}

export function min(array) {
  if (isNil(array) || array.length === 0) {
    return undefined
  }

  return Math.min(...array)
}

export function max(array) {
  if (isNil(array) || array.length === 0) {
    return undefined
  }

  return Math.max(...array)
}

export function first(array) {
  if (isNil(array)) {
    return
  }

  if (!isArrayLikeObject(array)) {
    console.warn(`first: unintended use with ${array}`)
    return
  }

  return array[0]
}

export function last(array) {
  if (isNil(array)) {
    return
  }

  if (!isArrayLikeObject(array)) {
    console.warn(`last: unintended use with ${array}`)
    return
  }

  return array[array.length - 1]
}

export function compact(input) {
  if (isNil(input)) {
    return []
  }

  if (!isObjectLike(input)) {
    console.warn(`compact: unintended use with ${input}`)
  }

  if (isArrayLikeObject(input)) {
    const accumulator = []

    for (let index = 0, length = input.length; index !== length; index++) {
      const val = input[index]

      if (!isNil(val)) {
        accumulator.push(val)
      }
    }

    return accumulator
  } else {
    const accumulator = {}

    for (const [key, val] of entries(input)) {
      if (!isNil(val)) {
        accumulator[key] = val
      }
    }

    return accumulator
  }
}

export function concat(...sources) {
  let accumulator = []

  for (const source of sources) {
    accumulator = accumulator.concat(source)
  }

  return accumulator
}

export function without(array, ...values) {
  return difference(array, values)
}

export function difference(leftArray, rightArray) {
  if (isNil(leftArray)) {
    return []
  }

  if (!isArray(leftArray)) {
    console.warn(`difference: unintended use with ${leftArray}`)
    return []
  }

  if (isNil(rightArray)) {
    return leftArray
  }

  if (!isArray(rightArray)) {
    console.warn(`difference: unintended use with ${rightArray}`)
    return leftArray
  }

  const accumulator = []

  for (const leftVal of leftArray) {
    if (!rightArray.includes(leftVal)) {
      accumulator.push(leftVal)
    }
  }

  return accumulator
}

export function intersection(leftArray, rightArray) {
  if (isNil(leftArray) || isNil(rightArray)) {
    return []
  }

  if (!isArray(leftArray)) {
    console.warn(`intersection: unintended use with ${leftArray}`)
    return []
  }

  if (!isArray(rightArray)) {
    console.warn(`intersection: unintended use with ${rightArray}`)
    return []
  }

  const accumulator = []

  for (const leftVal of leftArray) {
    if (rightArray.includes(leftVal)) {
      accumulator.push(leftVal)
    }
  }

  return accumulator
}

export function uniq(array) {
  if (isNil(array)) {
    return []
  }

  if (!isArrayLikeObject(array)) {
    console.warn(`uniq: unintended use with ${array}`)
    return []
  }

  const accumulator = []

  for (let index = 0, length = array.length; index !== length; index++) {
    const val = array[index]
    if (!accumulator.includes(val)) {
      accumulator.push(val)
    }
  }

  return accumulator
}

export function flatten(array) {
  if (isNil(array)) {
    return []
  }

  if (!isArrayLikeObject(array)) {
    console.warn(`flatten: unintended use with ${array}`)
    return []
  }

  let accumulator = []

  for (let index = 0, length = array.length; index !== length; index++) {
    const val = array[index]

    if (isArray(val)) {
      accumulator = accumulator.concat(val)
    } else {
      accumulator.push(val)
    }
  }

  return accumulator
}

export function includesAny(target, values) {
  if (isNil(target) || isNil(values)) {
    return false
  }

  if (!isArray(target)) {
    console.warn(`includesAny: unintended use with ${target}`)
    return false
  }

  if (!isArrayLikeObject(values)) {
    console.warn(`includesAny: unintended use with ${values}`)
    return false
  }

  for (let index = 0, length = values.length; index !== length; index++) {
    const val = values[index]
    if (target.includes(val)) {
      return true
    }
  }

  return false
}

export function chunk(array, chunkSize = 1) {
  if (isNil(array)) {
    return []
  }

  if (!(chunkSize >= 1)) {
    console.warn(`chunk: unintended use with ${chunkSize}`)
    return []
  }

  if (!isArray(array)) {
    console.warn(`chunk: unintended use with ${array}`)
    return []
  }

  const result = []

  for (let index = 0, length = array.length; index < length; index += chunkSize) {
    result.push(array.slice(index, index + chunkSize))
  }

  return result
}

// functions

export function once(action) {
  let result = undefined
  let isInvoked = false
  return function onceWrapper() {
    if (!isInvoked) {
      isInvoked = true
      result = action.apply(this, arguments)
      return result
    } else {
      return result
    }
  }
}

export function attempt(action, ...args) {
  try {
    return action(...args)
  } catch (err) {
    return err
  }
}

// utils

let uniqueIdCount = 0

export function uniqueId(input = '') {
  return '' + input + uniqueIdCount++
}

export function range(from, to) {
  if (isNil(from) && isNil(to)) {
    return []
  }

  if (isNil(to)) {
    to = from
    from = 0
  }

  const result = []

  if (from < to) {
    for (let index = from; index < to; index++) {
      result.push(index)
    }
  } else if (from > to) {
    for (let index = from; index > to; index--) {
      result.push(index)
    }
  }

  return result
}

export function random(first, second) {
  if (Number.isFinite(first) && Number.isFinite(second)) {
    return first + Math.floor(Math.random() * (second - first + 1))
  } else if (Number.isFinite(first)) {
    return random(0, first)
  } else {
    return random(0, 1)
  }
}

export function sample(array) {
  if (!isArrayLikeObject(array)) {
    return
  }

  const index = random(0, array.length - 1)
  return array[index]
}

export function noop() {
  // noop
}

export function identity(value) {
  return value
}

export function escapeRegExp(value) {
  const re = /[\\^$.*+?()[\]{}|]/g
  if (re.test(value)) {
    return value.replace(re, '\\$&')
  } else {
    return value
  }
}

export function stubArray() {
  return []
}

export function defaultTo(...values) {
  for (const val of values) {
    if (!isNil(val)) {
      return val
    }
  }
}

export function property(prop) {
  return function propertyReader(obj) {
    return obj && prop && obj[prop]
  }
}

export function zeroIfNaN(value) {
  if (isNaN(value)) {
    return 0
  } else {
    return value
  }
}
